import template from './searchBox.html';
import '../searchBox.scss';

/**
 * @ngdoc controller
 * @name OdsSearchBox
 * @description
 * This component renders the search box.
 */
class OdsSearchBox {
  /**
   * @param {$timeout} $timeout  To trigger the search after waiting for the search bind to change.
   */
  constructor($timeout) {
    'ngInject';

    /**
     * The local reference to the `$timeout` service.
     * @type {$timeout}
     */
    this.$timeout = $timeout;
    /**
     * Flag that indicates if the search input is focused or not.
     * @type {Boolean}
     */
    this.inputSearchFocused = false;
    /**
     * The suggestion group selected by the user.
     * @type {Object}
     */
    this.selectedSuggestionGroup = null;
  }
  /**
   * Initialize variables.
   */
  $onInit() {
    if (typeof this.loading !== 'boolean') {
      this.loading = false;
    }

    if (angular.isUndefined(this.searchMinLength)) {
      this.searchMinLength = 2;
    }

    if (angular.isUndefined(this.suggestionsLimit)) {
      this.suggestionsLimit = 3;
    }

    if (angular.isUndefined(this.maxLength)) {
      this.maxLength = 256;
    }
  }
  /**
   * Each time the suggestions binding changes clear the selected suggestion group.
   */
  $onChanges({ suggestions }) {
    if (suggestions && suggestions.currentValue) {
      this.selectedSuggestionGroup = null;
    }
  }
  /**
   * Check if a certain suggestion is a group or not.
   *
   * @param {Object} suggestion  the suggestion to check.
   */
  isASuggestionGroup(suggestion) {
    return suggestion && angular.isArray(suggestion.values);
  }
  /**
   * Clear the selected suggestion group.
   */
  onBackToSearch() {
    this.selectedSuggestionGroup = null;
  }
  /**
   * Clear the search box.
   */
  onSearchClear() {
    this.search = '';
    this.onSearchTextChange({ search: this.search });
  }
  /**
   * Display all the suggestions of a group only if the count of suggestions if bigger
   * than the max number of suggestions per group to displayed.
   *
   * @param {Object} suggestionGroup  the suggestion group to view.
   */
  onViewAllClick(suggestionGroup) {
    if (suggestionGroup.values.length > this.suggestionsLimit) {
      this.selectedSuggestionGroup = suggestionGroup;
    }
  }
  /**
   * Set if the search input is focused or not.
   *
   * @param {Boolean} flag  if the search input is focused or not.
   */
  setInputSearchFocused(flag) {
    this.inputSearchFocused = flag;
  }
  /**
   * Check if we should show the suggestions.
   *
   * @return {Boolean}
   */
  shouldShowSuggestions() {
    return !this.loading &&
      this.suggestions &&
      this.search.length > this.searchMinLength &&
      this.inputSearchFocused;
  }
}

/**
 * @ngdoc component
 * @name odsSearchBox
 * @description
 * The search box component.
 */
export default {
  /**
   * The controller class for the component.
   * @type {OdsSearchBox}
   */
  controller: OdsSearchBox,
  /**
   * The HTML template for the component.
   * @type {String}
   */
  template,
  /**
   * Component bindings
   * @type {Object}
   * @property {Boolean}  loading             if the suggestions are being loaded.
   * @property {Number}   maxLength           the limit of characters the search box will take.
   * @property {Object}   search              the search text to be displayed.
   * @property {Number}   searchMinLength     the minimum length of the search text to look for results.
   * @property {Number}   searchDebounceTime  the search update model debounce time.
   * @property {Array}    suggestions         the suggestions to be displayed.
   * @property {Number}   suggestionsLimit    the max number of suggestions per group to displayed.
   * @property {String}   emptyPlaceholder    the empty text placeholder.
   * @property {String}   placeholder         input placeholder, if not present defaults to 'Search'.
   * @property {Function} onSearchBlur        callback when the search box lost focus.
   * @property {Function} onSearchTextChange  callback when the search text is changed.
   * @property {Function} onSearchTrigger     callback when the search is triggered.
   * @property {Function} onSuggestionClick   callback when a suggestion is clicked.
   */
  bindings: {
    loading: '<',
    maxLength: '<',
    search: '<',
    searchMinLength: '<',
    searchDebounceTime: '<',
    suggestions: '<',
    suggestionsLimit: '<',
    emptyPlaceholder: '@',
    placeholder: '@',
    onSearchBlur: '&',
    onSearchTextChange: '&',
    onSearchTrigger: '&',
    onSuggestionClick: '&',
  },
};
